import React, { Suspense, useCallback, useEffect, useMemo } from "react";
import { styled } from '@mui/material/styles';
import Header from "./framework/layout/Header";
import Sidebar from "./framework/layout/Sidebar";
import { Route, Routes, useLocation, useParams } from "react-router";
import { useAuth } from "./framework/Contexts/AuthContext";
import {
  Avatar,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CardHeader,
  Box,
  LinearProgress,
} from "@mui/material";
import ErrorBoundary from "./framework/helpers/ErrorBoundary";
import { deepOrange } from "@mui/material/colors";
import UserProfile from "./framework/UserProfile";
import {
  getLazyComponent,
  normalizeAccessibleRoutes,
  preloadComponent,
  type NormalizedAppRoute,
} from "./routing/appRouting";

const Root = styled('div')({
  display: "flex",
});

const Content = styled('main')(({ theme }) => ({
  display: "flex",
  backgroundColor: theme.palette.background.default,
  overflow: "auto",
  position: "relative",
  float: "right",
  flexFlow: "column",
  height: "100vh",
  width: "100%",
  overflowScrolling: "touch",
}));

const AppBarSpacer = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

const App: React.FC = () => {
  const Auth: any = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const navigationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const normalizedRoutes = useMemo(
    () => normalizeAccessibleRoutes(Auth.AccessibleApps ?? []),
    [Auth.AccessibleApps]
  );

  const componentByPath = useMemo(() => {
    const entries = new Map<string, string>();
    normalizedRoutes.forEach((route) => {
      entries.set(route.path, route.component);
      const basePath = route.path.split("/:")[0];
      // Only set basePath if not already mapped (exact routes take precedence)
      if (!entries.has(basePath)) {
        entries.set(basePath, route.component);
      }
      if (route.path.includes("/:")) {
        const expandedPath = route.path.replaceAll("/:", "/");
        if (!entries.has(expandedPath)) {
          entries.set(expandedPath, route.component);
        }
      }
    });
    return entries;
  }, [normalizedRoutes]);

  const prefetchConfig = useMemo(
    () => ({ componentByPath, onPrefetch: preloadComponent }),
    [componentByPath]
  );

  const clearNavigationTimer = useCallback(() => {
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
  }, []);

  const beginNavigation = useCallback(() => {
    clearNavigationTimer();
    setIsNavigating(true);
    // Safety timeout: clear progress bar if route never signals ready
    navigationTimerRef.current = setTimeout(() => {
      setIsNavigating(false);
    }, 15_000);
  }, [clearNavigationTimer]);

  const completeNavigation = useCallback(() => {
    clearNavigationTimer();
    setIsNavigating(false);
  }, [clearNavigationTimer]);

  const failNavigation = useCallback(() => {
    clearNavigationTimer();
    setIsNavigating(false);
  }, [clearNavigationTimer]);

  useEffect(() => {
    if (!Auth.AccessibleApps) {
      setIsNavigating(false);
    }
  }, [Auth.AccessibleApps]);

  return (
    <Root>
      <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        prefetch={prefetchConfig}
        onNavigateStart={beginNavigation}
      />
      <Content>
        <AppBarSpacer />
        {isNavigating && <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 1100 }} />}
        {Auth.AccessibleApps ? (
          <Routes>
            {normalizedRoutes.map((route: NormalizedAppRoute) => (
              <Route
                path={route.path}
                element={
                  <ErrorBoundary onError={failNavigation} resetKey={location.pathname}>
                    <DynamicLoader
                      component={route.component}
                      loadedProps={route.loadedProps}
                      onRouteReady={completeNavigation}
                    />
                  </ErrorBoundary>
                }
                key={route.key}
              />
            ))}
            <Route
              path="/profile"
              element={
                <ErrorBoundary onError={failNavigation} resetKey={location.pathname}>
                  <ProfileRoute onRouteReady={completeNavigation} />
                </ErrorBoundary>
              }
            />
            <Route
              path="*"
              element={<NotFoundRoute onRouteReady={completeNavigation} />}
            />
          </Routes>
        ) : (
          <div>Loading</div>
        )}
      </Content>
    </Root>
  );
};

export default App;

const RouteNotFound = () => {
  const Auth: any = useAuth();
  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar
            sx={{ bgcolor: deepOrange[500] }}
            aria-label="Error"
          >
            !
          </Avatar>
        }
        title="Error Accessing this route"
      />
      <CardContent>
        <Typography color="textSecondary" gutterBottom>
          You have No access to this location
        </Typography>
        <Typography variant="body2" component="p">
          Try to login if you believe you should have access
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          onClick={() => {
            Auth.showLogin();
          }}
          variant={"outlined"}
          color={"primary"}
        >
          Login
        </Button>
      </CardActions>
    </Card>
  );
};

const RouteReadySignal = ({
  onRouteReady,
}: {
  onRouteReady?: (path: string) => void;
}) => {
  const location = useLocation();

  useEffect(() => {
    onRouteReady?.(location.pathname);
  }, [location.pathname, onRouteReady]);

  return null;
};

const RouteLoadingFallback = () => (
  <Box sx={{ width: "100%", px: 2, py: 1 }}>
    <LinearProgress />
  </Box>
);

const DynamicLoader = (props: Pick<NormalizedAppRoute, "component" | "loadedProps"> & {
  onRouteReady?: (path: string) => void;
}) => {
  const LazyComponent = getLazyComponent(props.component);
  const routeParams = useParams();

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <RouteReadySignal onRouteReady={props.onRouteReady} />
      <LazyComponent
        loadedProps={props.loadedProps}
        routeParam={routeParams}
      />
    </Suspense>
  );
};

const ProfileRoute = ({ onRouteReady }: { onRouteReady?: (path: string) => void }) => {
  return (
    <>
      <RouteReadySignal onRouteReady={onRouteReady} />
      <UserProfile />
    </>
  );
};

const NotFoundRoute = ({ onRouteReady }: { onRouteReady?: (path: string) => void }) => {
  return (
    <>
      <RouteReadySignal onRouteReady={onRouteReady} />
      <RouteNotFound />
    </>
  );
};
